import type React from "react";
import { useState } from "react";
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import BookingItem from "./BookingItem";
import { isBefore } from "date-fns/isBefore";
import { cancelBooking, updateBooking } from "../../apis/bookingApi";
import type { BookingsByUser } from "../../schemas/bookings";

interface BookingItemProps extends BookingsByUser {
  hasBeenRescheduled?: boolean;
}

export const isPastBooking = (booking: BookingItemProps): boolean => {
  const endTime = new Date(booking.endTime);
  const now = new Date();
  return isBefore(endTime, now);
};

const BookingsSection: React.FC<{
  bookings: BookingItemProps[];
  email: string;
  onBookingsUpdate?: (updatedBookings: BookingItemProps[]) => void;
}> = ({ bookings, email, onBookingsUpdate }) => {
  const [tabValue, setTabValue] = useState(0);
  const [localBookings, setLocalBookings] =
    useState<BookingItemProps[]>(bookings);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  // Use the state version of bookings for all operations
  const upcomingBookings = localBookings.filter(
    (booking) =>
      !isPastBooking(booking) &&
      booking.statusHistoryData.bookingStatus !== "cancelled" &&
      booking.statusHistoryData.bookingStatus !== "temporary"
  );

  const pastBookings = localBookings.filter(
    (booking) => isPastBooking(booking) || booking.statusHistoryData.bookingStatus === "cancelled"
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCancelBooking = async (
    booking: BookingItemProps,
    bookingId: string
  ) => {
    try {
      // First, check if the booking should be eligible for refund
      const bookingDate = new Date(booking.startTime); // Using startTime instead of bookingDate
      const currentDate = new Date();
      const timeDifference = bookingDate.getTime() - currentDate.getTime();
      const hoursDifference = timeDifference / (1000 * 60 * 60); // Convert milliseconds to hours
      const isWithin24Hours = hoursDifference < 24;
      const isAdminBooking = booking.payment.chargeId.startsWith("admin_");

      // Always update the booking status in the database
      await updateBooking(
        bookingId,
        "cancelled",
        email,
        booking.laneOrCage.name,
        booking.userDetails.firstName,
        booking.userDetails.lastName,
        booking.payment.totalHours,
        booking.payment.totalAmount,
        booking.bookingForDate,
        new Date(booking.startTime).toLocaleTimeString( 'en-US', { hour: '2-digit', minute: '2-digit' }),
        new Date(booking.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      );

      // Only process refund if booking is NOT within 24 hours AND has a non-zero amount
      if (!isWithin24Hours && !isAdminBooking) {
        // Initiate refund only in this case
        await cancelBooking(
          booking.payment.chargeId,
          email,
          booking.laneOrCage.name,
          booking.userDetails.firstName,
          booking.userDetails.lastName,
          booking.payment.totalHours,
          booking.payment.totalAmount,
          booking.bookingDate.toString(),
          booking.startTime.toString(),
          booking.endTime.toString()
        );
        setSnackbarSeverity("success");
        setSnackbarMessage(
          "Your booking has been successfully cancelled with refund"
        );
      } else {
        // No refund case
        let message = "Your booking has been successfully cancelled";

        // Add specific message about why no refund was given
        if (isWithin24Hours && !isAdminBooking) {
          message += " (no refund - less than 24 hours before start time)";
        }

        setSnackbarSeverity("success");
        setSnackbarMessage(message);
      }

      // Update local state
      const updatedBookings = localBookings.map((booking) =>
        booking.bookingId === bookingId
          ? { ...booking, statusHistoryData: { id: "cancelled", bookingStatus: "cancelled", expiresAt: new Date() } }
          : booking
      );

      setLocalBookings(updatedBookings);

      // Notify parent component if callback provided
      if (onBookingsUpdate) {
        onBookingsUpdate(updatedBookings);
      }

      setSnackbarOpen(true);
    } catch (err) {
      console.error("Error cancelling booking:", err);
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to cancel booking. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        My Bookings
      </Typography>

      <Paper elevation={3} sx={{ padding: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="booking tabs"
          >
            <Tab label={`Upcoming (${upcomingBookings.length})`} />
            <Tab label={`Past (${pastBookings.length})`} />
            <Tab
              label={`All (${
                localBookings.filter(
                  (booking) => booking.statusHistoryData.bookingStatus !== "temporary"
                ).length
              })`}
            />
          </Tabs>
        </Box>

        <Box role="tabpanel" hidden={tabValue !== 0}>
          {upcomingBookings.length > 0 ? (
            upcomingBookings.map((booking) => (
              <BookingItem
                key={booking.id}
                booking={booking}
                onCancelBooking={() =>
                  handleCancelBooking(booking, booking.id)
                }
              />
            ))
          ) : (
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ py: 3 }}
            >
              No upcoming bookings found.
            </Typography>
          )}
        </Box>

        <Box role="tabpanel" hidden={tabValue !== 1}>
          {pastBookings.length > 0 ? (
            pastBookings.map((booking) => (
              <BookingItem
                key={booking.bookingId}
                booking={booking}
                // onCancelBooking={() => handleCancelBooking(booking, booking.id)}
              />
            ))
          ) : (
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ py: 3 }}
            >
              No past bookings found.
            </Typography>
          )}
        </Box>

        <Box role="tabpanel" hidden={tabValue !== 2}>
          {localBookings.map((booking) => (
            <BookingItem
              key={booking.id}
              booking={booking}
              onCancelBooking={() =>
                handleCancelBooking(booking, booking.id)
              }
            />
          ))}
        </Box>

        {/* Notification for cancellation results */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default BookingsSection;
